import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { 
  FiHome, FiDatabase, FiFileText, FiCalendar, FiCpu, 
  FiSettings, FiUsers, FiActivity, FiBarChart2, 
  FiTarget, FiZap, FiBook, FiLogOut, FiGrid
} = FiIcons;

const Sidebar = () => {
  const [expandedCategory, setExpandedCategory] = useState(null);

  const toggleCategory = (category) => {
    if (expandedCategory === category) {
      setExpandedCategory(null);
    } else {
      setExpandedCategory(category);
    }
  };

  const menuItems = [
    { 
      name: 'Dashboard', 
      icon: FiHome, 
      path: '/' 
    },
    { 
      category: 'Assessment',
      icon: FiDatabase,
      items: [
        { name: 'Question Bank', icon: FiDatabase, path: '/question-bank' },
        { name: 'Quick Generate', icon: FiZap, path: '/paper-generator' },
        { name: 'Generated Papers', icon: FiFileText, path: '/generated-papers' },
        { name: 'Templates', icon: FiCpu, path: '/templates' },
      ]
    },
    { 
      category: 'Analytics',
      icon: FiBarChart2,
      items: [
        { name: 'Item Analysis', icon: FiTarget, path: '/item-analysis' },
        { name: 'Scoreboard', icon: FiActivity, path: '/scoreboard' },
      ]
    },
    { 
      category: 'Organization',
      icon: FiGrid,
      items: [
        { name: 'Exam Calendar', icon: FiCalendar, path: '/exam-calendar' },
        { name: 'CLO Management', icon: FiBook, path: '/clo-management' },
        { name: 'Team', icon: FiUsers, path: '/team' },
      ]
    },
    { 
      name: 'Settings', 
      icon: FiSettings, 
      path: '/profile' 
    },
  ];

  return (
    <motion.div 
      initial={{ x: -280 }}
      animate={{ x: 0 }}
      className="fixed left-0 top-0 h-screen w-72 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 z-20 pt-16"
    >
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto py-4 px-4">
          <div className="space-y-1">
            {menuItems.map((item, index) => (
              item.category ? (
                <div key={item.category} className="mb-2">
                  <button
                    className={`flex items-center justify-between w-full px-4 py-3 text-left text-sm font-medium rounded-lg transition-colors ${
                      expandedCategory === item.category
                        ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                        : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50'
                    }`}
                    onClick={() => toggleCategory(item.category)}
                  >
                    <div className="flex items-center">
                      <span className="mr-3">
                        <SafeIcon icon={item.icon} className={expandedCategory === item.category ? 'text-primary-600 dark:text-primary-400' : ''} />
                      </span>
                      <span>{item.category}</span>
                    </div>
                    <span className={`transform transition-transform duration-200 ${expandedCategory === item.category ? 'rotate-180' : ''}`}>
                      ▼
                    </span>
                  </button>
                  
                  <AnimatePresence>
                    {expandedCategory === item.category && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="mt-1 pl-10 space-y-1"
                      >
                        {item.items.map((subItem) => (
                          <NavLink
                            key={subItem.name}
                            to={subItem.path}
                            className={({ isActive }) => `
                              flex items-center px-4 py-2 text-sm rounded-lg transition-colors
                              ${isActive
                                ? 'text-primary-700 bg-primary-50 dark:text-primary-400 dark:bg-primary-900/30 font-medium'
                                : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50'
                              }
                            `}
                          >
                            <span className="mr-3">
                              <SafeIcon icon={subItem.icon} />
                            </span>
                            <span>{subItem.name}</span>
                          </NavLink>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <NavLink
                  key={item.name}
                  to={item.path}
                  className={({ isActive }) => `
                    flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors
                    ${isActive
                      ? 'text-primary-700 bg-primary-50 dark:text-primary-400 dark:bg-primary-900/30'
                      : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50'
                    }
                  `}
                >
                  <span className="mr-3">
                    <SafeIcon icon={item.icon} />
                  </span>
                  <span>{item.name}</span>
                </NavLink>
              )
            ))}
          </div>
        </div>
        
        {/* Logout button */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <button className="flex items-center w-full px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50 rounded-lg transition-colors">
            <span className="mr-3 text-red-500">
              <SafeIcon icon={FiLogOut} />
            </span>
            <span>Logout</span>
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default Sidebar;